import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb, { getBreadcrumbSchema } from '@/components/feature/Breadcrumb';
import VideoCard from '@/components/feature/VideoCard';
import { allVideos } from '@/mocks/videos';

const categoryMap: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  variety: '综艺',
  anime: '动漫',
  documentary: '纪录片',
};

export default function Article() {
  const { id } = useParams<{ id: string }>();

  const video = useMemo(() => allVideos.find((v) => v.id === id), [id]);

  const relatedVideos = useMemo(() => {
    if (!video) return [];
    return allVideos
      .filter((v) => v.id !== video.id && v.category === video.category)
      .slice(0, 4);
  }, [video]);

  if (!video) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-16 flex flex-col items-center justify-center min-h-[60vh] px-4">
            <div className="w-20 h-20 flex items-center justify-center rounded-2xl bg-background-100 text-foreground-400 mb-6">
              <i className="ri-error-warning-line text-4xl"></i>
            </div>
            <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">内容未找到</h2>
            <p className="text-sm text-foreground-500 mb-6">该影视内容可能已下架或不存在</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', href: '/' },
    { label: categoryMap[video.category] || video.category, href: `/category/${video.category}` },
    { label: video.title },
  ];

  const pageSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '神马视频',
        url: 'https://www.shenmashipin.com',
      },
      {
        '@type': 'WebSite',
        name: '神马视频',
        url: 'https://www.shenmashipin.com',
      },
      {
        '@type': 'Movie',
        name: video.title,
        description: video.description,
        image: video.imageUrl,
        url: `https://www.shenmashipin.com/article/${video.id}`,
        datePublished: video.updateDate,
        actor: video.cast.map((name) => ({
          '@type': 'Person',
          name,
        })),
        countryOfOrigin: {
          '@type': 'Country',
          name: video.region,
        },
      },
      getBreadcrumbSchema(breadcrumbItems),
    ],
  };

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title={`${video.title} - 神马视频 | ${video.source} ${video.year} ${categoryMap[video.category]}在线观看`}
        description={`${video.title}：${video.description.slice(0, 100)}。主演：${video.cast.join('、')}。${video.source}在线观看，${categoryMap[video.category]}高清全集不缓冲。`}
        keywords={`${video.title},${video.source},${categoryMap[video.category]},${video.tags.join(',')},在线观看,高清,全集不缓冲`}
        canonicalUrl={`https://www.shenmashipin.com/article/${video.id}`}
        ogType="video.movie"
        ogImage={video.imageUrl}
        schema={pageSchema}
      />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-16">
          <Breadcrumb />

          <article className="px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-[900px] mx-auto">
              <div className="relative rounded-2xl overflow-hidden mb-8">
                <img
                  src={video.imageUrl}
                  alt={video.title}
                  className="w-full aspect-video object-cover"
                  title={`${video.title} - ${video.source}在线观看`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-1 bg-primary-500 text-background-50 text-xs rounded-md font-bold whitespace-nowrap">
                      {video.rating}
                    </span>
                    <span className="px-2.5 py-1 bg-foreground-900/70 text-background-50 text-xs rounded-md whitespace-nowrap">
                      {categoryMap[video.category]}
                    </span>
                    <span className="px-2.5 py-1 bg-foreground-900/70 text-background-50 text-xs rounded-md whitespace-nowrap">
                      {video.year}
                    </span>
                    <span className="px-2.5 py-1 bg-foreground-900/70 text-background-50 text-xs rounded-md whitespace-nowrap">
                      {video.region}
                    </span>
                  </div>
                </div>
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
                {video.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-6">
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i> {video.views} 次播放
                </span>
                <span>时长：{video.duration}</span>
                <span>更新：{video.updateDate}</span>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-6">
                {video.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-accent-100 text-accent-700 text-xs rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-8 text-sm">
                <span className="text-foreground-500 whitespace-nowrap">主演：</span>
                {video.cast.map((actor) => (
                  <span key={actor} className="text-foreground-700 whitespace-nowrap">
                    {actor}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-5 mb-8">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">剧情简介</h2>
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {video.description}
                </p>
              </div>

              <div className="bg-background-100 rounded-xl p-5 mb-8">
                <h2 className="font-heading text-lg font-bold text-foreground-900 mb-3">播放来源</h2>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                    <i className="ri-play-circle-fill text-xl"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-800">{video.source}</p>
                    <p className="text-xs text-foreground-500">点击前往 {video.source} 观看完整内容</p>
                  </div>
                  <a
                    href={video.sourceUrl}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="ml-auto inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap"
                  >
                    <i className="ri-external-link-line"></i>
                    前往观看
                  </a>
                </div>
              </div>

              {relatedVideos.length > 0 && (
                <div>
                  <h2 className="font-heading text-xl font-bold text-foreground-900 mb-5">
                    相关推荐
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {relatedVideos.map((v) => (
                      <VideoCard key={v.id} videoId={v.id} size="small" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </article>
        </div>
        <Footer />
      </div>
    </>
  );
}